/**
 * R-14 T-087 / T-104 — experience tiers, the pure half (no React, no fetch). A user's tier (1 Beginner
 * … 4 Expert) and the disclosure registry (element id, app, minimum tier) decide what the shell
 * shows; an element with no registry row and no declared minimum is Expert-only. Roles bound first:
 * the declaration is already filtered to the caller's roles, so a tier can only hide, never widen.
 * Hidden is not removed — the route still answers and the server still refuses by role.
 */
import type { AppsResponse, MenuDecl, MenuItemDecl } from '@/features/apps/apps-api';
import type { Tier } from '@/kit';

export type { Tier };
export const BEGINNER: Tier = 1;
export const EXPERT: Tier = 4;

export interface DisclosureRow {
  elementId: string;
  app: string;
  minTier: Tier;
  note?: string | null;
}

/** The registry's app for L0 entries and the shell's own actions (`shell.<name>`). */
export const PLATFORM_APP = 'platform';

const asTier = (n: unknown): Tier | null => (n === 1 || n === 2 || n === 3 || n === 4 ? n : null);

/** The element id a declared item is read under (L2: the item's id). */
export const itemElement = (it: MenuItemDecl) => it.elementId ?? it.id;

/** The current app's row › the platform's row › the declaration's own minimum › Expert. */
export function minTierFor(rows: readonly DisclosureRow[], id: string, declared?: unknown, app?: string): Tier {
  const mine = rows.filter((r) => r.elementId === id);
  const row = mine.find((r) => r.app === app) ?? mine.find((r) => r.app === PLATFORM_APP) ?? mine[0];
  return asTier(row?.minTier) ?? asTier(declared) ?? EXPERT;
}

/**
 * The user's tier as the API answered it (it carries the app's instance default: CIGS answers Beginner,
 * R-15 T-107); else the app's default when the web knows one; else Expert, so a failed or absent read
 * never hides every element (PR #78).
 */
export function resolveTier(answered: unknown, appDefault?: unknown): Tier {
  return asTier(answered) ?? asTier(appDefault) ?? EXPERT;
}

/** The kit's DisclosureValue.minTier. */
export type MinTier = (id: string) => Tier;

/** The minimum tiers the module declarations carry on their items (L2's optional `minTier`), by element id. */
export function declaredTiers(apps: AppsResponse | undefined): Map<string, unknown> {
  const out = new Map<string, unknown>();
  const add = (items: readonly MenuItemDecl[]) => {
    for (const it of items) if (it.minTier !== undefined) out.set(itemElement(it), it.minTier);
  };
  add(apps?.platform.entries ?? []);
  // R-16 T-135 (PR #88): the shell's own elements carry the platform's default, so an app with no rows of its own still has one
  for (const e of apps?.platform.elements ?? []) out.set(e.id, e.minTier);
  for (const a of apps?.apps ?? []) add(a.menuBar.flatMap((m) => m.items));
  return out;
}

export function menuItemsAt(items: readonly MenuItemDecl[], tier: Tier, min: MinTier): MenuItemDecl[] {
  return items.filter((it) => tier >= min(itemElement(it)));
}

/**
 * An app's menu bar at a tier: each item reads its own row; a menu with nothing left is hidden below
 * Expert (a business user sees no empty menus), and kept at Expert so the standard order stays. The
 * app menu (the app's name) always stays: it says which app the user is in.
 */
export function menuBarAt(menus: readonly MenuDecl[], tier: Tier, min: MinTier): MenuDecl[] {
  return menus.map((m) => ({ ...m, items: menuItemsAt(m.items, tier, min) })).filter((m) => tier >= EXPERT || m.id === 'app' || m.items.length > 0);
}

/**
 * T-068: a declared route carries `{scopeKey}` (the chosen scope's key). Filled when one is chosen;
 * otherwise cut back to the part before it, where the app offers its own chooser (`/department`).
 */
export function fillScope(route: string, scopeKey: string | null | undefined): string {
  if (!route.includes('{scopeKey}')) return route;
  return scopeKey ? route.replaceAll('{scopeKey}', encodeURIComponent(scopeKey)) : route.slice(0, route.indexOf('/{scopeKey}')) || '/';
}

export function menuBarScoped(menus: readonly MenuDecl[], scopeKey: string | null | undefined): MenuDecl[] {
  return menus.map((m) => ({ ...m, items: m.items.map((it) => ({ ...it, route: fillScope(it.route, scopeKey) })) }));
}

/** The shell's own actions that read a tier, for the registry page's known elements. */
export const SHELL_ELEMENTS: readonly { id: string; label: string }[] = [
  { id: 'shell.ask', label: 'Ask (top bar)' },
  { id: 'shell.find', label: 'Find anything (top bar)' },
  { id: 'shell.notifications', label: 'Notifications (top bar)' },
];

export const TIER_LABEL: Record<Tier, string> = { 1: 'Beginner', 2: 'Tier 2', 3: 'Tier 3', 4: 'Expert' };

/** Every element the shell knows: the declarations' menu items and the shell actions. */
export function knownElements(apps: AppsResponse | undefined): { elementId: string; app: string; where: string }[] {
  const out: { elementId: string; app: string; where: string }[] = [];
  for (const e of apps?.platform.entries ?? []) out.push({ elementId: itemElement(e), app: PLATFORM_APP, where: `Top bar › ${e.label}` });
  for (const a of apps?.apps ?? []) for (const m of a.menuBar) for (const it of m.items) out.push({ elementId: itemElement(it), app: a.slug, where: `${a.name} › ${m.label} › ${it.label}` });
  for (const s of SHELL_ELEMENTS) out.push({ elementId: s.id, app: PLATFORM_APP, where: s.label });
  return out;
}
